import React, { useState, useEffect } from 'react';
import { CreditCard, Calendar, CheckCircle, AlertCircle, Clock, Loader2, ExternalLink, Receipt, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentPage = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [accessLevel, setAccessLevel] = useState(null);

  useEffect(() => {
    fetchPaymentData();
  }, []);

  const fetchPaymentData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      
      // Fetch payment history
      const historyResponse = await fetch(`${API_URL}/api/payments/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (historyResponse.ok) {
        const historyData = await historyResponse.json();
        setPaymentHistory(historyData.payments || []);
      }

      // Fetch access level
      const accessResponse = await fetch(`${API_URL}/api/access/level`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (accessResponse.ok) {
        const accessData = await accessResponse.json();
        setAccessLevel(accessData);
      }

      // Fetch subscription status
      const subResponse = await fetch(`${API_URL}/api/payments/subscription`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (subResponse.ok) {
        const subData = await subResponse.json();
        setSubscription(subData);
      }

    } catch (error) {
      console.error('Error fetching payment data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMonthlyPayment = async () => {
    setProcessingPayment(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_URL}/api/payments/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          payment_type: 'monthly_mentorship',
          amount: 120,
          currency: 'gbp'
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to initiate payment');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Failed to process payment. Please try again.');
    } finally {
      setProcessingPayment(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const styles = {
      completed: 'bg-green-100 text-green-700',
      pending: 'bg-yellow-100 text-yellow-700',
      initiated: 'bg-blue-100 text-blue-700',
      failed: 'bg-red-100 text-red-700'
    };
    return styles[status] || styles.pending;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  const nextPaymentDate = subscription?.next_payment_date 
    ? new Date(subscription.next_payment_date)
    : new Date(new Date().setMonth(new Date().getMonth() + 1));

  const isActive = accessLevel?.is_mentorship_member || accessLevel?.membership_tier === 'mentorship';

  return (
    <div className="min-h-screen bg-gray-50 pt-4 pb-8 sm:py-8" data-testid="payment-page">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header - Added safe area for mobile */}
        <div className="mb-8 pt-safe">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Payment & Subscription</h1>
          <p className="text-gray-600">Manage your mentorship payments and view history</p>
        </div>

        {/* Subscription Status */}
        <div className="bg-white rounded-2xl border p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-full ${isActive ? 'bg-green-100' : 'bg-gray-100'}`}>
                {isActive ? (
                  <CheckCircle className="w-8 h-8 text-green-600" />
                ) : (
                  <AlertCircle className="w-8 h-8 text-gray-400" />
                )}
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {isActive ? 'Active Mentorship' : 'No Active Subscription'}
                </h2>
                <p className="text-gray-600">
                  {isActive 
                    ? `Tier: ${accessLevel?.membership_tier || 'Mentorship'}`
                    : 'Subscribe to access all mentorship features'}
                </p>
              </div>
            </div>
            
            {isActive && (
              <span className="px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                Active
              </span>
            )}
          </div>

          {isActive && (
            <div className="mt-6 pt-6 border-t">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-purple-50 rounded-xl">
                  <Calendar className="w-5 h-5 text-purple-600 mb-2" />
                  <p className="text-sm text-gray-600">Next Payment Due</p>
                  <p className="text-lg font-semibold text-gray-900">
                    {formatDate(nextPaymentDate)}
                  </p>
                </div>
                <div className="p-4 bg-blue-50 rounded-xl">
                  <CreditCard className="w-5 h-5 text-blue-600 mb-2" />
                  <p className="text-sm text-gray-600">Monthly Amount</p>
                  <p className="text-lg font-semibold text-gray-900">£120</p>
                </div>
                <div className="p-4 bg-green-50 rounded-xl">
                  <Shield className="w-5 h-5 text-green-600 mb-2" />
                  <p className="text-sm text-gray-600">Status</p>
                  <p className="text-lg font-semibold text-gray-900">Active</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Make Payment */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-800 rounded-2xl p-6 mb-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-semibold mb-2">Monthly Mentorship Payment</h3>
              <p className="text-purple-100 mb-4">
                Continue your journey with full access to all mentorship features
              </p>
              <div className="flex items-center gap-4">
                <span className="text-3xl font-bold">£120</span>
                <span className="text-purple-200">/month</span>
              </div>
            </div>
            
            <Button
              onClick={handleMonthlyPayment}
              disabled={processingPayment}
              className="bg-white text-purple-700 hover:bg-purple-50"
              size="lg"
              data-testid="make-payment-btn"
            >
              {processingPayment ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5 mr-2" />
                  Make Payment
                </>
              )}
            </Button>
          </div>
          
          <div className="mt-4 pt-4 border-t border-purple-500 flex items-center gap-2 text-sm text-purple-200">
            <Shield className="w-4 h-4" />
            <span>Secure payment powered by Stripe</span>
          </div>
        </div>

        {/* Included Features */}
        <div className="bg-white rounded-2xl border p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Included in Mentorship</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              'Access to all courses and lessons',
              'One-on-one mentorship sessions',
              'Community access',
              'Assignment feedback',
              'Live Zoom sessions',
              'Library access (73+ resources)',
              'Progress tracking',
              'Certificate of completion'
            ].map((feature, i) => (
              <div key={i} className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                <span className="text-gray-700">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Payment History */}
        <div className="bg-white rounded-2xl border p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Payment History</h3>
            <Receipt className="w-5 h-5 text-gray-400" />
          </div>

          {paymentHistory.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No payment history yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {paymentHistory.map((payment, index) => (
                <div
                  key={payment.id || index}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-xl"
                >
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-white rounded-full">
                      <CreditCard className="w-5 h-5 text-gray-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {payment.description || 'Monthly Mentorship'}
                      </p>
                      <p className="text-sm text-gray-500">
                        {formatDate(payment.created_at || payment.date)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">
                      {formatCurrency(payment.amount || 100)}
                    </p>
                    <span className={`text-xs px-2 py-1 rounded-full ${getStatusBadge(payment.status)}`}>
                      {payment.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Help Section */}
        <div className="mt-6 text-center text-sm text-gray-500">
          <p>
            Having trouble with payments?{' '}
            <a href="/contact" className="text-purple-600 hover:underline">
              Contact Support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
